import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { LayoutSingleColumn, Page } from '../../components';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { savePaymentMethod } from '../../ducks/paymentMethods.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { convertUSDToSelected, getCurrencySymbol } from '../../util/currencyConversion';
import { pathByRouteName } from '../../util/routes';
import { checkPaymentStatus, initiateCharge } from '../CheckoutPage/CheckoutPage.duck.js';
import FooterContainer from '../FooterContainer/FooterContainer';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import css from './SubscriptionPlan.module.css';

import {
  cancelSubscription,
  changeSubscriptionPlan,
  confirmStripePaymentOnServer,
  creditWallet,
  fetchSubscriptionStatus,
  initializePayment,
  initializeStripeIntent,
  pauseSubscription,
  resumeSubscription,
  updatePayment,
} from './SubscriptionPlan.duck';

// Subcomponents
import SubscriptionPlanActiveCard from './SubscriptionPlanActiveCard';
import SubscriptionPlanDetails from './SubscriptionPlanDetails';
import SubscriptionPlanGrid from './SubscriptionPlanGrid';
import SubscriptionPlanHero from './SubscriptionPlanHero';
import SubscriptionPlanModals from './SubscriptionPlanModals';
import SubscriptionPlanTrial from './SubscriptionPlanTrial';

// ================ Local useReducer Setup ================

const localInitialState = {
  loadingPlanId: null,
  successOpen: false,
  successAmount: 0,
  selectedPlan: null,
  submitting: false,
  paymentModalOpen: false,
  cardGateway: 'tap',
  error: null,
  confirmModalOpen: false,
  confirmModalTitle: '',
  confirmModalMessage: '',
  confirmModalAction: null,
  confirmModalCancelText: '',
  confirmModalConfirmText: '',
  confirmModalIsAlert: false,
};

const localReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case 'SET_LOADING_PLAN_ID':
      return { ...state, loadingPlanId: payload };
    case 'SET_SUCCESS_OPEN':
      return { ...state, successOpen: payload };
    case 'SET_SUCCESS_AMOUNT':
      return { ...state, successAmount: payload };
    case 'SET_SELECTED_PLAN':
      return { ...state, selectedPlan: payload };
    case 'SET_SUBMITTING':
      return { ...state, submitting: payload };
    case 'SET_PAYMENT_MODAL_OPEN':
      return { ...state, paymentModalOpen: payload };
    case 'SET_CARD_GATEWAY':
      return { ...state, cardGateway: payload };
    case 'SET_ERROR':
      return { ...state, error: payload };
    case 'SHOW_CONFIRM_MODAL':
      return {
        ...state,
        confirmModalOpen: true,
        confirmModalTitle: payload.title || '',
        confirmModalMessage: payload.message || '',
        confirmModalCancelText: payload.cancelText || '',
        confirmModalConfirmText: payload.confirmText || '',
        confirmModalIsAlert: !!payload.isAlert,
        confirmModalAction: payload.action || null,
      };
    case 'CLOSE_CONFIRM_MODAL':
      return {
        ...state,
        confirmModalOpen: false,
        confirmModalTitle: '',
        confirmModalMessage: '',
        confirmModalCancelText: '',
        confirmModalConfirmText: '',
        confirmModalIsAlert: false,
        confirmModalAction: null,
      };
    default:
      return state;
  }
};

// ================ Page Component ================

const SubscriptionPlanComponent = props => {
  const {
    scrollingDisabled,
    currentUser,
    selectedCurrency,
    fxRates,
    subStatus,
    loadingSub,
    paymentStatusData,
    onFetchCurrentUser,
    onFetchSubStatus,
    onCancelSubscription,
    onPauseSubscription,
    onResumeSubscription,
    onChangeSubscriptionPlan,
    onSavePaymentMethod,
    onUpdateProfile,
    onCheckPaymentStatus,
    onInitiatePayment,
    onManageDisableScrolling,
    onUpdatePayment,
    onCreditWallet,
    onInitializePayment,
    onInitializeStripeIntent,
    onConfirmStripePayment,
  } = props;

  const [localState, dispatchLocal] = useReducer(localReducer, localInitialState);
  const {
    loadingPlanId,
    successOpen,
    successAmount,
    selectedPlan,
    submitting,
    paymentModalOpen,
    cardGateway,
    error,
    confirmModalOpen,
    confirmModalTitle,
    confirmModalMessage,
    confirmModalAction,
    confirmModalCancelText,
    confirmModalConfirmText,
    confirmModalIsAlert,
  } = localState;

  const history = useHistory();
  const config = useConfiguration();
  const location = useLocation();
  const intl = useIntl();
  const routeConfiguration = useRouteConfiguration();

  const selectedPlanRef = useRef(null);
  const plansSectionRef = useRef(null);
  const statusCheckedRef = useRef(null);

  // Stable user ID — used as dep instead of the full currentUser object.
  const currentUserIdForSub = currentUser?.id?.uuid;

  // Sync user stripe defaults — dep on user ID only, NOT onFetchCurrentUser.
  // onFetchCurrentUser comes from mapDispatchToProps as an inline arrow → new ref every render.
  // Including it in deps would cause this effect to re-run on every Redux state update.
  useEffect(() => {
    if (currentUserIdForSub) {
      onFetchCurrentUser({
        callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
      }).catch(() => {});
    }
  }, [currentUserIdForSub]);

  // Guard child and tutor view access
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isTutor =
    userType === 'tutor' ||
    userType === 'teacher' ||
    userType === 'provider' ||
    userType === 'professional' ||
    (userType && userType !== 'student');
  useEffect(() => {
    if (isFamilyChild || isTutor) {
      history.replace('/');
    }
  }, [isFamilyChild, isTutor, history]);

  if (isFamilyChild || isTutor) {
    return null;
  }

  // Fetch subscription status — dep on user ID only, NOT onFetchSubStatus.
  // onFetchSubStatus comes from mapDispatchToProps as an inline arrow → new ref every render.
  // Including it as a dep causes an infinite fetch loop whenever Redux state updates.
  useEffect(() => {
    if (currentUserIdForSub) {
      onFetchSubStatus({ userId: currentUserIdForSub }).catch(() => {});
    }
  }, [currentUserIdForSub]);

  // Modal alert controllers
  const showAlertModal = useCallback(
    (title, message) => {
      dispatchLocal({
        type: 'SHOW_CONFIRM_MODAL',
        payload: {
          title,
          message,
          confirmText: intl.formatMessage({
            id: 'SubscriptionPlan.confirm.btn.ok',
            defaultMessage: 'OK',
          }),
          isAlert: true,
          action: () => dispatchLocal({ type: 'CLOSE_CONFIRM_MODAL' }),
        },
      });
    },
    [intl]
  );

  const showConfirmModal = useCallback(
    (title, message, onConfirm) => {
      dispatchLocal({
        type: 'SHOW_CONFIRM_MODAL',
        payload: {
          title,
          message,
          cancelText: intl.formatMessage({
            id: 'SubscriptionPlan.confirm.btn.cancel',
            defaultMessage: 'Cancel',
          }),
          confirmText: intl.formatMessage({
            id: 'SubscriptionPlan.confirm.btn.confirm',
            defaultMessage: 'Confirm',
          }),
          isAlert: false,
          action: () => {
            dispatchLocal({ type: 'CLOSE_CONFIRM_MODAL' });
            if (onConfirm) onConfirm();
          },
        },
      });
    },
    [intl]
  );

  // Pause / Resume / Cancel subscription hooks
  const handleCancelSubscription = async () => {
    const userId = currentUser?.id?.uuid;
    if (!userId) return;

    const confirmMsg = subStatus?.isInRemorseWindow
      ? intl.formatMessage({ id: 'SubscriptionPlan.activeCard.confirmCancelRemorse' })
      : intl.formatMessage({ id: 'SubscriptionPlan.activeCard.confirmCancelStandard' });

    showConfirmModal(
      intl.formatMessage({
        id: 'SubscriptionPlan.confirm.title',
        defaultMessage: 'Confirm Action',
      }),
      confirmMsg,
      async () => {
        try {
          const res = await onCancelSubscription({ userId });
          if (res?.success) {
            showAlertModal(
              intl.formatMessage({
                id: 'SubscriptionPlan.alert.success',
                defaultMessage: 'Success',
              }),
              res.message || 'Subscription cancelled successfully.'
            );
          }
        } catch (err) {
          showAlertModal(
            intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
            err?.message || 'Failed to cancel subscription.'
          );
        }
      }
    );
  };

  const handlePauseSubscription = async () => {
    const userId = currentUser?.id?.uuid;
    if (!userId) return;

    const confirmMsg = intl.formatMessage({ id: 'SubscriptionPlan.activeCard.confirmPause' });

    showConfirmModal(
      intl.formatMessage({
        id: 'SubscriptionPlan.confirm.title',
        defaultMessage: 'Confirm Action',
      }),
      confirmMsg,
      async () => {
        try {
          const res = await onPauseSubscription({ userId });
          if (res?.success) {
            showAlertModal(
              intl.formatMessage({
                id: 'SubscriptionPlan.alert.success',
                defaultMessage: 'Success',
              }),
              res.message || 'Subscription paused successfully.'
            );
          }
        } catch (err) {
          showAlertModal(
            intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
            err?.message || 'Failed to pause subscription.'
          );
        }
      }
    );
  };

  const handleResumeSubscription = async () => {
    const userId = currentUser?.id?.uuid;
    if (!userId) return;

    const confirmMsg = intl.formatMessage({ id: 'SubscriptionPlan.activeCard.confirmResume' });

    showConfirmModal(
      intl.formatMessage({
        id: 'SubscriptionPlan.confirm.title',
        defaultMessage: 'Confirm Action',
      }),
      confirmMsg,
      async () => {
        try {
          const res = await onResumeSubscription({ userId });
          if (res?.success) {
            showAlertModal(
              intl.formatMessage({
                id: 'SubscriptionPlan.alert.success',
                defaultMessage: 'Success',
              }),
              res.message || 'Subscription resumed successfully.'
            );
          }
        } catch (err) {
          showAlertModal(
            intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
            err?.message || 'Failed to resume subscription.'
          );
        }
      }
    );
  };

  // Payment Redirect Status Checking
  const Uparams = new URLSearchParams(location.search);
  const tapId = Uparams.get('tap_id');

  // Only re-run when tapId changes. onCheckPaymentStatus and showAlertModal are
  // mapDispatchToProps / useCallback refs — new on every render — so they must
  // NOT be listed as deps or this fires in an infinite loop.
  useEffect(() => {
    if (tapId) {
      onCheckPaymentStatus({ paymentId: tapId }).catch(error => {
        console.error('Error fetching payment status:', error);
        showAlertModal(
          intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
          intl.formatMessage({ id: 'CheckoutPage.somethingWentWrong' })
        );
      });
    }
  }, [tapId]);

  const isVerify = paymentStatusData?.status === 'CAPTURED';
  const isAuthorized = paymentStatusData?.status === 'AUTHORIZED';
  const isSubscriptionPlan = paymentStatusData?.metadata?.type === 'subscription_plan';
  const isCreditPackage = paymentStatusData?.metadata?.type === 'credit_package';
  const isQuickCharge = isSubscriptionPlan || isCreditPackage;

  useEffect(() => {
    let isMounted = true;
    const confirmPayment = async () => {
      const isReady = isAuthorized || isVerify;
      if (isReady && tapId && isQuickCharge) {
        if (statusCheckedRef.current === tapId) {
          console.log('[SubscriptionPlan] Tap payment status already checked for tapId:', tapId);
          return;
        }
        statusCheckedRef.current = tapId;
        try {
          dispatchLocal({ type: 'SET_SUBMITTING', payload: true });
          const planId = paymentStatusData?.metadata?.planId;
          const credits = Number(paymentStatusData?.metadata?.credits) || 0;
          const baseCredits = Number(paymentStatusData?.metadata?.baseCredits) || credits;
          const bonusCredits = Number(paymentStatusData?.metadata?.bonusCredits) || 0;
          const initIdForUpdate =
            paymentStatusData?.metadata?.initId ||
            window.__lastInitId ||
            (typeof sessionStorage !== 'undefined'
              ? sessionStorage.getItem('lastInitId')
              : undefined);

          await onUpdatePayment({
            initId: initIdForUpdate,
            customerId: paymentStatusData?.customer?.id,
            amount: paymentStatusData?.amount,
            currency: paymentStatusData?.currency,
            merchantId: paymentStatusData?.merchant?.id,
            status: paymentStatusData?.status,
            paymentId: paymentStatusData?.id,
            customerName: `${paymentStatusData?.customer?.first_name} ${paymentStatusData?.customer?.last_name}`,
            planId: planId,
            credits: credits,
            userId: currentUser?.id?.uuid,
            email: currentUser?.attributes?.email,
            paymentAgreementId: paymentStatusData?.payment_agreement?.id,
            cardId: paymentStatusData?.source?.id,
          });

          // Add credits to wallet after successful payment confirmation
          try {
            const userId = currentUser?.id?.uuid;
            const userEmail = currentUser?.attributes?.email;
            if (userId && userEmail && credits > 0) {
              if (isSubscriptionPlan) {
                await onCreditWallet({
                  userId,
                  userEmail,
                  amount: credits,
                  source: 'deposit',
                  referenceId: `plan_${planId}_${paymentStatusData?.id}`,
                  description: intl.formatMessage(
                    { id: 'SubscriptionPlan.wallet.description' },
                    {
                      planName: paymentStatusData?.metadata?.planName || planId,
                      amount: credits,
                    }
                  ),
                  metadata: {
                    planId: planId,
                    planName: paymentStatusData?.metadata?.planName,
                    paymentId: paymentStatusData?.id,
                  },
                });
              } else {
                await onCreditWallet({
                  userId,
                  userEmail,
                  amount: baseCredits,
                  source: 'deposit',
                  referenceId: `package_${planId}_${paymentStatusData?.id}`,
                  description: `Purchase of credit package ${planId.toUpperCase()}`,
                  metadata: {
                    planId: planId,
                    paymentId: paymentStatusData?.id,
                  },
                });
                if (bonusCredits > 0) {
                  await onCreditWallet({
                    userId,
                    userEmail,
                    amount: bonusCredits,
                    source: 'reward',
                    referenceId: `bonus_${planId}_${paymentStatusData?.id}`,
                    description: `Bonus credits for package ${planId.toUpperCase()} purchase`,
                    metadata: {
                      planId: planId,
                      paymentId: paymentStatusData?.id,
                    },
                  });
                }
              }
            }
          } catch (e) {
            console.warn('[SubscriptionPlan] walletCredit failed:', e);
          }

          try {
            await onFetchCurrentUser({ enforce: true });
            const userId = currentUser?.id?.uuid;
            if (userId) {
              await onFetchSubStatus({ userId });
            }
          } catch (fetchErr) {
            console.warn('[SubscriptionPlan] fetchCurrentUser/SubStatus failed:', fetchErr);
          }

          if (isMounted) {
            dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
            const walletPath = pathByRouteName('WalletPage', routeConfiguration);
            history.push(walletPath);
          }
        } catch (err) {
          if (isMounted) {
            dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
            dispatchLocal({
              type: 'SET_ERROR',
              payload: 'An error occurred while processing the payment.',
            });
          }
        }
      }
    };

    confirmPayment();
    return () => {
      isMounted = false;
    };
  }, [
    isVerify,
    isAuthorized,
    isSubscriptionPlan,
    isCreditPackage,
    isQuickCharge,
    tapId,
    // NOTE: Do NOT add currentUser (object), paymentStatusData (object), or
    // onFetchCurrentUser / onFetchSubStatus / onCreditWallet (mapDispatchToProps arrows) here.
    // All of those are new references on every Redux state update and would
    // cause this effect to re-run in an infinite loop.
    // The statusCheckedRef guard ensures the async work only runs once per tapId.
  ]);

  const handleScrollToPlans = useCallback(event => {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    if (plansSectionRef.current) {
      plansSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Form submission: Tap Gateway
  const handleSubmitTap = async (values, planToCharge) => {
    // Fallback to the ref in case the state-derived prop is stale/null at call time
    const plan = planToCharge || selectedPlanRef.current;
    if (!plan) {
      console.error('[handleSubmitTap] planToCharge is null — aborting');
      return;
    }
    if (submitting) return;
    dispatchLocal({ type: 'SET_SUBMITTING', payload: true });

    try {
      const { data: tapCard, useSavedCard, paymentAgreementId } = values;
      const { email, profile } = currentUser?.attributes || {};
      const { firstName, lastName, publicData } = profile || {};
      const { customerId } = publicData || {};

      const planUsdPrice = Number(plan.price.replace('USD ', ''));
      const conv = convertUSDToSelected(planUsdPrice, selectedCurrency, fxRates);
      // Round charge amount up to upper whole number (e.g. 183.01 -> 184) to match displayed plan price
      const upperAmount = Math.ceil(conv.amount);
      const amount = Math.round(upperAmount * 100);
      const currency = conv.currency;

      const initId = uuidv4();
      window.__lastInitId = initId;
      try {
        sessionStorage.setItem('lastInitId', initId);
      } catch (_) {}

      // Pre-create record
      try {
        await onInitializePayment({
          initId,
          customerId: customerId || '',
          amount,
          currency,
          merchantId: 'tap',
          customerName: `${firstName} ${lastName}`,
          planId: plan.id,
          credits: plan.credits,
        });
      } catch (e) {
        console.warn('initPaymentInDB failed:', e);
      }

      const hasValidPhone = currentUser?.attributes?.profile?.protectedData?.phoneNumber;
      const phoneNum = hasValidPhone
        ? currentUser.attributes.profile.protectedData.phoneNumber.replace('+', '')
        : '';
      const countryCodeNum = phoneNum ? phoneNum.substring(0, 3) : '';

      const tokenId = tapCard?.id || values.cardId;

      const authData = {
        amount: amount / 100,
        // When a saved card's paymentAgreementId is present, this is a merchant-initiated
        // transaction — skip 3DS and card-saving prompts entirely.
        // Without it (new card), require 3DS and save for future use.
        customer_initiated: paymentAgreementId ? false : true,
        threeDSecure: paymentAgreementId ? false : true,
        save_card: paymentAgreementId ? false : true,
        ...(paymentAgreementId ? { payment_agreement: { id: paymentAgreementId } } : {}),
        currency,
        metadata: {
          initId,
          type: 'subscription_plan',
          planId: plan.id,
          planName: plan.name,
          credits: plan.credits,
          userId: currentUser?.id?.uuid,
          email: currentUser?.attributes?.email,
        },
        customer: {
          ...(customerId ? { id: customerId } : {}),
          email,
          ...(hasValidPhone
            ? {
                phone: {
                  country_code: countryCodeNum,
                  number: phoneNum,
                },
              }
            : {}),
          first_name: firstName,
          last_name: lastName,
        },
        merchant: {
          id: process.env.REACT_APP_TAP_MERCHANT_ID,
        },
        source: {
          id: tokenId,
        },
        authorize_debit: false,
        auto: {
          type: 'VOID',
          time: 720,
        },
        redirect: {
          url: `${process.env.REACT_APP_MARKETPLACE_ROOT_URL}/subscription-plan`,
        },
      };

      const createdCharge = await onInitiatePayment(authData);
      const chargeId = createdCharge?.id;
      const chargeStatus = createdCharge?.status;

      if (!chargeId) {
        showAlertModal(
          intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
          'Payment authorization failed. Please try again.'
        );
        dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
        return;
      }

      const isPaymentSuccessful = chargeStatus === 'AUTHORIZED' || chargeStatus === 'CAPTURED';
      if (isPaymentSuccessful) {
        await onUpdatePayment({
          initId,
          customerId: createdCharge?.customer?.id || customerId || '',
          amount: createdCharge?.amount || amount / 100,
          currency: createdCharge?.currency || currency,
          merchantId: createdCharge?.merchant?.id || process.env.REACT_APP_TAP_MERCHANT_ID,
          status: chargeStatus,
          paymentId: chargeId,
          customerName: `${firstName} ${lastName}`,
          planId: plan.id,
          credits: plan.credits,
          userId: currentUser?.id?.uuid,
          email: currentUser?.attributes?.email,
          paymentAgreementId: paymentAgreementId || createdCharge?.payment_agreement?.id,
          cardId: tapCard?.id || createdCharge?.source?.id || values.cardId,
        });

        const newPaymentAgreementId = paymentAgreementId || createdCharge?.payment_agreement?.id;
        const newCardId = tapCard?.id || createdCharge?.source?.id || values.cardId;
        const newTapCustomerId = createdCharge?.customer?.id || customerId;

        if (newPaymentAgreementId && newCardId && !paymentAgreementId) {
          try {
            const freshUser = await onFetchCurrentUser({ enforce: true });
            const oldCards = freshUser?.attributes?.profile?.protectedData?.cards || [];
            const cardExists = oldCards.some(c => c.cardId === newCardId);
            if (!cardExists) {
              const updatedCards = [
                ...oldCards,
                { paymentAgreementId: newPaymentAgreementId, cardId: newCardId },
              ];
              await onUpdateProfile({
                publicData: { customerId: newTapCustomerId },
                protectedData: { cards: updatedCards },
              });
            }
          } catch (e) {
            console.warn('Failed to save Tap card to profile:', e);
          }
        }

        try {
          await onFetchCurrentUser({ enforce: true });
          const userId = currentUser?.id?.uuid;
          if (userId) {
            await onFetchSubStatus({ userId });
          }
        } catch (fetchErr) {
          console.warn('[SubscriptionPlan] fetchCurrentUser/SubStatus failed:', fetchErr);
        }

        const userId = currentUser?.id?.uuid;
        const userEmail = currentUser?.attributes?.email;
        const creditsAmount = plan.credits;

        if (userId && userEmail && creditsAmount > 0) {
          await onCreditWallet({
            userId,
            userEmail,
            amount: creditsAmount,
            source: 'deposit',
            referenceId: `plan_${plan.id}_${chargeId}`,
            description: intl.formatMessage(
              { id: 'SubscriptionPlan.wallet.description' },
              {
                planName: plan.name,
                amount: creditsAmount,
              }
            ),
            metadata: {
              planId: plan.id,
              planName: plan.name,
              paymentId: chargeId,
            },
          });

          dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
          dispatchLocal({ type: 'SET_PAYMENT_MODAL_OPEN', payload: false });
          dispatchLocal({ type: 'SET_SUCCESS_AMOUNT', payload: creditsAmount });
          dispatchLocal({ type: 'SET_SUCCESS_OPEN', payload: true });

          setTimeout(() => {
            dispatchLocal({ type: 'SET_SUCCESS_OPEN', payload: false });
            const walletPath = pathByRouteName('WalletPage', routeConfiguration);
            history.push(walletPath);
          }, 1200);
        }
      } else if (chargeStatus === 'INITIATED' && createdCharge?.transaction?.url) {
        window.location.href = createdCharge.transaction.url;
      } else {
        dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
      }
    } catch (error) {
      console.error('Error in handleSubmitTap:', error);
      dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
      showAlertModal(
        intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
        'Payment failed: ' + (error?.message || 'Unknown error')
      );
    }
  };

  // Form submission: Stripe Gateway
  const handleSubmitStripe = async ({ stripe, card, savedPaymentMethodId }, planToCharge) => {
    if (submitting) return;
    // Fallback to the ref in case the state-derived prop is stale/null at call time
    const plan = planToCharge || selectedPlanRef.current;
    if (!plan) {
      console.error('[handleSubmitStripe] planToCharge is null — aborting');
      return;
    }
    dispatchLocal({ type: 'SET_SUBMITTING', payload: true });

    try {
      if (!currentUser) {
        dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
        return;
      }

      const planUsdPrice = Number(plan.price.replace('USD ', ''));
      const conv = convertUSDToSelected(planUsdPrice, selectedCurrency, fxRates);
      // Round charge amount up to upper whole number (e.g. 183.01 -> 184) to match displayed plan price
      const upperAmount = Math.ceil(conv.amount);
      const amount = Math.round(upperAmount * 100);
      const currency = conv.currency;

      if (!amount || !currency) {
        showAlertModal(
          intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
          intl.formatMessage({ id: 'CheckoutPageWithPayment.amountMissingAlert' }) ||
            'Amount is missing'
        );
        dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
        return;
      }

      const { email, profile } = currentUser?.attributes || {};
      const { firstName, lastName, publicData } = profile || {};
      const { customerId } = publicData || {};

      const initId = uuidv4();
      window.__lastInitId = initId;
      try {
        sessionStorage.setItem('lastInitId', initId);
      } catch (_) {}

      try {
        await onInitializePayment({
          initId,
          customerId: customerId || '',
          amount,
          currency,
          merchantId: 'stripe',
          customerName: `${firstName} ${lastName}`,
          planId: plan.id,
          credits: plan.credits,
        });
      } catch (e) {
        console.warn('initPaymentInDB failed:', e);
      }

      const intentRes = await onInitializeStripeIntent({
        amount,
        currency,
        userId: currentUser?.id?.uuid,
        initId,
        credits: plan.credits,
        email: currentUser?.attributes?.email,
        stripeCustomerId: currentUser?.stripeCustomer?.attributes?.stripeCustomerId,
        isNewCard: !savedPaymentMethodId,
      });

      if (!intentRes?.clientSecret) {
        showAlertModal(
          intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
          'Failed to retrieve Stripe PaymentIntent'
        );
        dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
        return;
      }

      const confirmParams = savedPaymentMethodId
        ? { payment_method: savedPaymentMethodId }
        : {
            payment_method: {
              card: card,
              billing_details: {
                name: `${firstName} ${lastName}`,
                email: email,
              },
            },
          };

      const confirmResult = await stripe.confirmCardPayment(intentRes.clientSecret, confirmParams);

      if (confirmResult.error) {
        alert(confirmResult.error.message);
        dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
        return;
      }

      const paymentIntent = confirmResult.paymentIntent;
      if (paymentIntent.status === 'succeeded') {
        let savedStripeCustomer = null;
        if (paymentIntent.payment_method && !savedPaymentMethodId) {
          try {
            await onSavePaymentMethod(null, paymentIntent.payment_method);
            const finalUser = await onFetchCurrentUser({ enforce: true });
            savedStripeCustomer = finalUser?.stripeCustomer;
          } catch (saveErr) {
            console.warn('Failed to save Stripe payment method:', saveErr);
          }
        }

        const confirmServerRes = await onConfirmStripePayment({
          paymentIntentId: paymentIntent.id,
          initId,
          userId: currentUser?.id?.uuid,
          email,
          credits: plan.credits,
          customerName: `${firstName} ${lastName}`,
          planId: plan.id,
          stripeCustomerId:
            savedStripeCustomer?.attributes?.stripeCustomerId ||
            currentUser?.stripeCustomer?.attributes?.stripeCustomerId,
          paymentMethodId: paymentIntent.payment_method,
        });

        if (confirmServerRes?.success) {
          try {
            await onFetchCurrentUser({ enforce: true });
            const userId = currentUser?.id?.uuid;
            if (userId) {
              await onFetchSubStatus({ userId });
            }
          } catch (fetchErr) {
            console.warn('[SubscriptionPlan] fetchCurrentUser/SubStatus failed:', fetchErr);
          }

          dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
          dispatchLocal({ type: 'SET_PAYMENT_MODAL_OPEN', payload: false });
          dispatchLocal({ type: 'SET_SUCCESS_AMOUNT', payload: plan.credits });
          dispatchLocal({ type: 'SET_SUCCESS_OPEN', payload: true });

          setTimeout(() => {
            dispatchLocal({ type: 'SET_SUCCESS_OPEN', payload: false });
            const walletPath = pathByRouteName('WalletPage', routeConfiguration);
            history.push(walletPath);
          }, 1200);
        }
      }
    } catch (error) {
      console.error('Error in handleSubmitStripe:', error);
      dispatchLocal({ type: 'SET_SUBMITTING', payload: false });
      showAlertModal(
        intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
        'Payment failed: ' + (error?.message || 'Unknown error')
      );
    }
  };

  const handleSelectPlan = plan => {
    if (!currentUser) {
      showAlertModal(
        intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
        intl.formatMessage({ id: 'SubscriptionPlan.alert.signInRequired' })
      );
      return;
    }

    const isSub = [
      'starter',
      'regular',
      'plus',
      'premium',
      'lite',
      'standard',
      'intensive',
      'family',
    ].includes(plan.id);
    if (isSub && subStatus && (subStatus.status === 'active' || subStatus.status === 'past_due')) {
      if (
        subStatus.planId?.toLowerCase() === plan.id.toLowerCase() ||
        (subStatus.planId?.toLowerCase() === 'lite' && plan.id === 'starter') ||
        (subStatus.planId?.toLowerCase() === 'standard' && plan.id === 'regular') ||
        (subStatus.planId?.toLowerCase() === 'intensive' && plan.id === 'plus') ||
        (subStatus.planId?.toLowerCase() === 'family' && plan.id === 'premium')
      ) {
        if (subStatus.nextPrice != null) {
          showConfirmModal(
            intl.formatMessage({
              id: 'SubscriptionPlan.confirm.title',
              defaultMessage: 'Confirm Action',
            }),
            `Would you like to cancel your scheduled change and stay on your current ${
              plan.name
            } plan ($${plan.price.replace('USD ', '')}/month)?`,
            async () => {
              try {
                const res = await onChangeSubscriptionPlan({
                  userId: currentUser?.id?.uuid,
                  planId: null,
                });
                showAlertModal(
                  intl.formatMessage({
                    id: 'SubscriptionPlan.alert.success',
                    defaultMessage: 'Success',
                  }),
                  res.message || 'Scheduled change cancelled.'
                );
              } catch (err) {
                showAlertModal(
                  intl.formatMessage({
                    id: 'SubscriptionPlan.alert.error',
                    defaultMessage: 'Error',
                  }),
                  err?.message || 'Failed to cancel scheduled change.'
                );
              }
            }
          );
        }
        return;
      }

      if (subStatus.isInRemorseWindow) {
        showAlertModal(
          intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
          intl.formatMessage({ id: 'SubscriptionPlan.remorseChangeWarning' })
        );
        return;
      }

      const planNameLocalized =
        intl.formatMessage({ id: `SubscriptionPlan.planName.${plan.id}` }) || `${plan.name} Plan`;
      const formattedPrice = `$${plan.price.replace('USD ', '')}`;
      const nextCycleDate = new Date(subStatus.currentPeriodEnd).toLocaleDateString();

      showConfirmModal(
        intl.formatMessage({
          id: 'SubscriptionPlan.confirm.title',
          defaultMessage: 'Confirm Action',
        }),
        intl.formatMessage(
          { id: 'SubscriptionPlan.activeCard.confirmChangePlan' },
          {
            planName: planNameLocalized,
            price: formattedPrice,
            date: nextCycleDate,
          }
        ),
        async () => {
          try {
            const res = await onChangeSubscriptionPlan({
              userId: currentUser?.id?.uuid,
              planId: plan.id,
            });
            showAlertModal(
              intl.formatMessage({
                id: 'SubscriptionPlan.alert.success',
                defaultMessage: 'Success',
              }),
              res.message || 'Plan change scheduled successfully.'
            );
          } catch (err) {
            showAlertModal(
              intl.formatMessage({ id: 'SubscriptionPlan.alert.error', defaultMessage: 'Error' }),
              err?.message || 'Failed to schedule plan change.'
            );
          }
        }
      );
      return;
    }

    selectedPlanRef.current = plan;
    dispatchLocal({ type: 'SET_SELECTED_PLAN', payload: plan });
    dispatchLocal({ type: 'SET_PAYMENT_MODAL_OPEN', payload: true });
  };

  const renderPrice = priceStr => {
    const planUsd = Number(priceStr.replace('USD ', ''));
    if (selectedCurrency === 'USD') {
      return `$${Math.ceil(planUsd)}`;
    }
    const conv = convertUSDToSelected(planUsd, selectedCurrency, fxRates);
    // Always round up any fractional value to the upper whole number (e.g. 183.01 -> 184)
    const upperAmount = Math.ceil(conv.amount);
    return `${getCurrencySymbol(selectedCurrency)}${upperAmount}`;
  };

  const getFeatureContent = (feature, plan) => {
    if (
      typeof feature === 'string' &&
      (feature.includes('added to wallet') || feature.includes('added to wallet per month'))
    ) {
      const formatted = renderPrice(`USD ${plan.credits}`);
      return feature.replace(/<strong>.*?<\/strong>/, `<strong>${formatted}</strong>`);
    }
    return feature;
  };

  const plans = [
    {
      id: 'lite',
      name: intl.formatMessage({ id: 'SubscriptionPlan.plan.Lite.name', defaultMessage: 'Lite' }),
      credits: 52.5,
      price: 'USD 50',
      description: intl.formatMessage({
        id: 'SubscriptionPlan.plan.Lite.desc',
        defaultMessage: 'Perfect for light learning schedules',
      }),
      features: [
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Lite.feature.1',
          defaultMessage: '<strong>$52.50</strong> added to wallet per month',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Lite.feature.2',
          defaultMessage: 'Includes 5% subscriber bonus',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Lite.feature.3',
          defaultMessage: '+ 3% one-time signup bonus',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Lite.feature.4',
          defaultMessage: 'Cancel anytime',
        }),
      ],
      buttonText: intl.formatMessage({
        id: 'SubscriptionPlan.plan.selectButton',
        defaultMessage: 'Select Plan',
      }),
      popular: false,
      popularBadge: false,
    },
    {
      id: 'standard',
      name: intl.formatMessage({
        id: 'SubscriptionPlan.plan.Standard.name',
        defaultMessage: 'Standard',
      }),
      credits: 107,
      price: 'USD 100',
      description: intl.formatMessage({
        id: 'SubscriptionPlan.plan.Standard.desc',
        defaultMessage: 'Our most popular learning tier',
      }),
      features: [
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Standard.feature.1',
          defaultMessage: '<strong>$107.00</strong> added to wallet per month',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Standard.feature.2',
          defaultMessage: 'Includes 7% subscriber bonus',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Standard.feature.3',
          defaultMessage: '+ 5% one-time signup bonus',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Standard.feature.4',
          defaultMessage: 'Cancel anytime',
        }),
      ],
      buttonText: intl.formatMessage({
        id: 'SubscriptionPlan.plan.selectButton',
        defaultMessage: 'Select Plan',
      }),
      popular: true,
      popularBadge: true,
    },
    {
      id: 'intensive',
      name: intl.formatMessage({
        id: 'SubscriptionPlan.plan.Intensive.name',
        defaultMessage: 'Intensive',
      }),
      credits: 220,
      price: 'USD 200',
      description: intl.formatMessage({
        id: 'SubscriptionPlan.plan.Intensive.desc',
        defaultMessage: 'For dedicated and accelerated learning',
      }),
      features: [
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Intensive.feature.1',
          defaultMessage: '<strong>$220.00</strong> added to wallet per month',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Intensive.feature.2',
          defaultMessage: 'Includes 10% subscriber bonus',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Intensive.feature.3',
          defaultMessage: '+ 7% one-time signup bonus',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Intensive.feature.4',
          defaultMessage: 'Cancel anytime',
        }),
      ],
      buttonText: intl.formatMessage({
        id: 'SubscriptionPlan.plan.selectButton',
        defaultMessage: 'Select Plan',
      }),
      popular: false,
      popularBadge: false,
    },
    {
      id: 'family',
      name: intl.formatMessage({
        id: 'SubscriptionPlan.plan.Family.name',
        defaultMessage: 'Family',
      }),
      credits: 448,
      price: 'USD 400',
      description: intl.formatMessage({
        id: 'SubscriptionPlan.plan.Family.desc',
        defaultMessage: 'Perfect for family learning needs',
      }),
      features: [
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Family.feature.1',
          defaultMessage: '<strong>$448.00</strong> added to wallet per month',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Family.feature.2',
          defaultMessage: 'Includes 12% subscriber bonus',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Family.feature.3',
          defaultMessage: '+ 10% one-time signup bonus',
        }),
        intl.formatMessage({
          id: 'SubscriptionPlan.plan.Family.feature.4',
          defaultMessage: 'Cancel anytime',
        }),
      ],
      buttonText: intl.formatMessage({
        id: 'SubscriptionPlan.plan.selectButton',
        defaultMessage: 'Select Plan',
      }),
      popular: false,
      popularBadge: false,
    },
  ];

  const planList = [
    {
      icon: (
        <svg
          width="36"
          height="36"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21.3955 9.53491H30.6978M23.3539 12.7907H30.6978M21.3955 16.0465H26.0467"
            stroke="#00674F"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <path
            d="M14.7204 20.9302H34.8837V4.65112H5.11621V17.2724"
            stroke="#00674F"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7.90691 20.9302H5.11621V17.2092C5.11621 15.6679 6.3657 14.4185 7.90691 14.4185H15.3488H18.4917L22.3585 10.5518L22.6875 10.8806C23.5957 11.7888 23.5957 13.2612 22.6875 14.1695L20.0564 16.8006C19.927 16.9304 19.7732 17.0333 19.6038 17.1034C19.4345 17.1735 19.253 17.2095 19.0697 17.2092H15.3488V20.9302H7.90691ZM7.90691 20.9302V17.2092"
            stroke="#D64000"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.30273 8.83715C9.30273 7.80961 10.1357 6.97668 11.1632 6.97668H12.0934C13.121 6.97668 13.9539 7.80961 13.9539 8.83715V12.093H11.1632C10.1357 12.093 9.30273 11.26 9.30273 10.2325V8.83715Z"
            stroke="#00674F"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="10.7" cy="26.05" r="2.32" stroke="#D64000" strokeWidth="1.2" />
          <path
            d="M6.05 34.88V33.95C6.05 32.16 7.5 30.7 9.3 30.7H12.09C13.89 30.7 15.35 32.16 15.35 33.95V34.88H6.05Z"
            stroke="#D64000"
            strokeWidth="1.2"
          />
          <circle cx="20" cy="26.05" r="2.32" stroke="#D64000" strokeWidth="1.2" />
          <path
            d="M15.35 34.88V33.95C15.35 32.16 16.8 30.7 18.6 30.7H21.4C23.2 30.7 24.65 32.16 24.65 33.95V34.88H15.35Z"
            stroke="#D64000"
            strokeWidth="1.2"
          />
          <circle cx="29.3" cy="26.05" r="2.32" stroke="#D64000" strokeWidth="1.2" />
          <path
            d="M24.65 34.88V33.95C24.65 32.16 26.1 30.7 27.9 30.7H30.7C32.5 30.7 33.95 32.16 33.95 33.95V34.88H24.65Z"
            stroke="#D64000"
            strokeWidth="1.2"
          />
        </svg>
      ),
      heading: intl.formatMessage({ id: 'SubscriptionPlan.planList.1.heading' }),
      subHeading: intl.formatMessage({ id: 'SubscriptionPlan.planList.1.subHeading' }),
    },
    {
      icon: (
        <svg
          width="36"
          height="36"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.0152 8.84366L20.0059 8.85297L19.9966 8.84366C18.1353 6.98236 15.697 6.05176 13.2587 6.05176C10.8204 6.05176 8.3821 6.98236 6.5208 8.84366C2.78889 12.557 2.78889 18.5969 6.5115 22.3195L20.0059 35.8139"
            stroke="#00674F"
            strokeWidth="1.2"
            strokeMiterlimit="14"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M19.9971 8.84371L20.0064 8.85302L20.0157 8.84371C22.1468 6.70325 25.0412 5.79125 27.8238 6.10762C29.8991 6.34027 31.9094 7.24306 33.4915 8.84371C35.3528 10.705 36.2834 13.1433 36.2834 15.5817C36.2834 18.0199 35.3528 20.4582 33.4915 22.3195L20.0064 35.8139M20.0002 24.6065C20.5162 24.5072 20.9528 24.4577 21.2505 24.4577C22.6098 24.4577 23.7212 25.5591 23.7212 26.9284C23.7212 28.2977 22.6198 29.3991 21.2505 29.3991C20.9528 29.3991 20.5063 29.3495 20.0002 29.2502V35.8139M20.0002 24.6046V17.6223C19.4942 17.7216 19.0477 17.7712 18.75 17.7712C18.4253 17.772 18.1037 17.7086 17.8036 17.5847C17.5034 17.4608 17.2308 17.2789 17.0012 17.0493C16.7716 16.8197 16.5896 16.547 16.4658 16.2469C16.3419 15.9468 16.2785 15.6251 16.2793 15.3005C16.2793 13.9312 17.3907 12.8298 18.75 12.8298C19.0477 12.8298 19.4842 12.8793 20.0002 12.9786V8.8372"
            stroke="#D64000"
            strokeWidth="1.2"
            strokeMiterlimit="14"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      heading: intl.formatMessage({ id: 'SubscriptionPlan.planList.2.heading' }),
      subHeading: intl.formatMessage({ id: 'SubscriptionPlan.planList.2.subHeading' }),
    },
    {
      icon: (
        <svg
          width="36"
          height="36"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M33.5999 13.9442C34.4278 15.7954 34.8836 17.8419 34.8836 20C34.8836 28.214 28.2139 34.8837 19.9999 34.8837C15.6092 34.8837 11.665 32.9861 8.93945 29.9628"
            stroke="#00674F"
            strokeWidth="1.2"
            strokeMiterlimit="16"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.9019 29.5162H8.52051V33.8883"
            stroke="#00674F"
            strokeWidth="1.2"
            strokeMiterlimit="16"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.39993 26.0559C5.57203 24.2047 5.11621 22.1582 5.11621 20.0001C5.11621 11.7861 11.786 5.11633 19.9999 5.11633C24.3906 5.11633 28.3348 7.01401 31.0604 10.0373"
            stroke="#D64000"
            strokeWidth="1.2"
            strokeMiterlimit="16"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M27.1816 10.6604H31.563V6.28833"
            stroke="#D64000"
            strokeWidth="1.2"
            strokeMiterlimit="16"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      heading: intl.formatMessage({ id: 'SubscriptionPlan.planList.3.heading' }),
      subHeading: intl.formatMessage({ id: 'SubscriptionPlan.planList.3.subHeading' }),
    },
  ];

  const membershipList = [
    {
      countColor: '#00674F',
      heading: intl.formatMessage({ id: 'SubscriptionPlan.membershipList.1.heading' }),
      subHeading: intl.formatMessage({ id: 'SubscriptionPlan.membershipList.1.subHeading' }),
    },
    {
      countColor: '#D64000',
      heading: intl.formatMessage({ id: 'SubscriptionPlan.membershipList.2.heading' }),
      subHeading: intl.formatMessage({ id: 'SubscriptionPlan.membershipList.2.subHeading' }),
    },
    {
      countColor: '#0505A6',
      heading: intl.formatMessage({ id: 'SubscriptionPlan.membershipList.3.heading' }),
      subHeading: intl.formatMessage({ id: 'SubscriptionPlan.membershipList.3.subHeading' }),
    },
    {
      countColor: '#FEC220',
      heading: intl.formatMessage({ id: 'SubscriptionPlan.membershipList.4.heading' }),
      subHeading: intl.formatMessage({ id: 'SubscriptionPlan.membershipList.4.subHeading' }),
    },
  ];

  const policiesList = [
    {
      icon: <IconCard type="auto" />,
      heading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.cycle.heading',
        defaultMessage: 'Monthly Cycle',
      }),
      subHeading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.cycle.text',
        defaultMessage: 'Charged monthly on the same calendar date as sign-up.',
      }),
    },
    {
      icon: <IconCard type="safe" />,
      heading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.cancel.heading',
        defaultMessage: 'Cancel Anytime & Keep Your Balance',
      }),
      subHeading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.cancel.text',
        defaultMessage:
          'Cancelling stops all future recurring charges immediately. Your existing wallet balance remains 100% available to spend on lessons and is never removed, forfeited, or lost.',
      }),
    },
    {
      icon: <IconCard type="limitless" />,
      heading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.remorse.heading',
        defaultMessage: '14-Day Remorse Window',
      }),
      subHeading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.remorse.text',
        defaultMessage:
          'A 14-day buyer’s-remorse window applies to each renewal charge. The charge is fully reversible for 14 days.',
      }),
    },
    {
      icon: <IconCard type="personalized" />,
      heading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.min.heading',
        defaultMessage: 'Flexible Subscription',
      }),
      subHeading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.min.text',
        defaultMessage:
          'Minimum subscription is $50/month. You can change your monthly amount anytime; the change takes effect starting next cycle.',
      }),
    },
    {
      icon: <IconCard type="limitless" />,
      heading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.expiry.heading',
        defaultMessage: 'Expiry & Spending',
      }),
      subHeading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.expiry.text',
        defaultMessage:
          'Base funds never expire; each month’s bonus carries its own 90-day clock. Your wallet automatically spends bonus funds first to protect your balance.',
      }),
    },
    {
      icon: <IconCard type="safe" />,
      heading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.hierarchy.heading',
        defaultMessage: 'Bonus Hierarchy',
      }),
      subHeading: intl.formatMessage({
        id: 'SubscriptionPlan.policy.hierarchy.text',
        defaultMessage:
          'Maximize your value across products: Pay-as-you-go (no bonus) < One-time top-ups (3–10% bonus) < Subscribe & Save (5–12% bonus) < Family plan (top tier).',
      }),
    },
  ];

  const policySubtitle = intl.formatMessage({
    id: 'SubscriptionPlan.policy.subtitle',
    defaultMessage:
      'Example: A $100/month subscriber receives $107 of lesson value every month — about $84 extra a year versus paying as they go.',
  });

  const payinTotal = (() => {
    if (!selectedPlan) return null;
    const planUsdPrice = Number(selectedPlan.price.replace('USD ', ''));
    const conv = convertUSDToSelected(planUsdPrice, selectedCurrency, fxRates);
    // Align payinTotal minor units with the upper whole number (e.g. 183.01 -> 184)
    const upperAmount = Math.ceil(conv.amount);
    return {
      amount: Math.round(upperAmount * 100),
      currency: conv.currency,
    };
  })();

  const title = intl.formatMessage({ id: 'SubscriptionPlan.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <TopbarContainer />
      <LayoutSingleColumn>
        <div className={css.pricingWrapper}>
          <div className={css.minWidthSection}>
            <SubscriptionPlanHero />

            <SubscriptionPlanActiveCard
              subStatus={subStatus}
              loadingSub={loadingSub}
              handleResumeSubscription={handleResumeSubscription}
              handleCancelSubscription={handleCancelSubscription}
            />

            <div ref={plansSectionRef}>
              <SubscriptionPlanGrid
                plans={plans}
                subStatus={subStatus}
                currentUser={currentUser}
                loadingPlanId={loadingPlanId}
                selectedPlan={selectedPlan}
                renderPrice={renderPrice}
                getFeatureContent={getFeatureContent}
                handleSelectPlan={handleSelectPlan}
              />
            </div>
          </div>

          <SubscriptionPlanDetails
            planList={planList}
            membershipList={membershipList}
            handleScrollToPlans={handleScrollToPlans}
          />

          <SubscriptionPlanTrial />
        </div>
      </LayoutSingleColumn>
      <FooterContainer />

      <SubscriptionPlanModals
        successOpen={successOpen}
        successAmount={successAmount}
        handleCloseSuccessModal={() => {
          dispatchLocal({ type: 'SET_SUCCESS_OPEN', payload: false });
          const walletPath = pathByRouteName('WalletPage', routeConfiguration);
          history.push(walletPath);
        }}
        paymentModalOpen={paymentModalOpen}
        selectedPlan={selectedPlan}
        cardGateway={cardGateway}
        handleClosePaymentModal={() => {
          dispatchLocal({ type: 'SET_PAYMENT_MODAL_OPEN', payload: false });
          dispatchLocal({ type: 'SET_SELECTED_PLAN', payload: null });
          selectedPlanRef.current = null;
        }}
        setCardGateway={gateway => dispatchLocal({ type: 'SET_CARD_GATEWAY', payload: gateway })}
        onInitiatePayment={onInitiatePayment}
        currentUser={currentUser}
        handleSubmitTap={handleSubmitTap}
        handleSubmitStripe={handleSubmitStripe}
        config={config}
        payinTotal={payinTotal}
        submitting={submitting}
        setSubmitting={val => dispatchLocal({ type: 'SET_SUBMITTING', payload: val })}
        renderPrice={renderPrice}
        confirmModalOpen={confirmModalOpen}
        confirmModalTitle={confirmModalTitle}
        confirmModalMessage={confirmModalMessage}
        confirmModalIsAlert={confirmModalIsAlert}
        confirmModalCancelText={confirmModalCancelText}
        confirmModalConfirmText={confirmModalConfirmText}
        confirmModalAction={confirmModalAction}
        handleCloseConfirmModal={() => dispatchLocal({ type: 'CLOSE_CONFIRM_MODAL' })}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { selectedCurrency, fxRates } = state.currency;
  const { subStatus, loadingSub } = state.SubscriptionPlan;
  const { data: paymentStatusData } = state.CheckoutPage;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    selectedCurrency: selectedCurrency || 'USD',
    fxRates,
    subStatus: subStatus || null,
    loadingSub: loadingSub || false,
    paymentStatusData,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchCurrentUser: params => dispatch(fetchCurrentUser(params)),
  onFetchSubStatus: params => dispatch(fetchSubscriptionStatus(params)),
  onCancelSubscription: params => dispatch(cancelSubscription(params)),
  onPauseSubscription: params => dispatch(pauseSubscription(params)),
  onResumeSubscription: params => dispatch(resumeSubscription(params)),
  onChangeSubscriptionPlan: params => dispatch(changeSubscriptionPlan(params)),
  onSavePaymentMethod: (id, method) => dispatch(savePaymentMethod(id, method)),
  onUpdateProfile: params => dispatch(updateProfile(params)),
  onCheckPaymentStatus: params => dispatch(checkPaymentStatus(params)),
  onInitiatePayment: params => dispatch(initiateCharge(params)),
  onUpdatePayment: params => dispatch(updatePayment(params)),
  onCreditWallet: params => dispatch(creditWallet(params)),
  onInitializePayment: params => dispatch(initializePayment(params)),
  onInitializeStripeIntent: params => dispatch(initializeStripeIntent(params)),
  onConfirmStripePayment: params => dispatch(confirmStripePaymentOnServer(params)),
  onManageDisableScrolling: (componentId, disable) =>
    dispatch(manageDisableScrolling(componentId, disable)),
});

const SubscriptionPlan = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SubscriptionPlanComponent);

export default SubscriptionPlan;
